import { scroll } from '@seek/ca-myprofile-utils';
import { useResponsiveValue } from 'braid-design-system';
import { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import type { Card } from 'src/types';

import { startScroll, endScroll } from './actionCreators';

interface Props {
  card: Card;
  component: React.ElementType;
}

interface RouteProps {
  duration?: number;
  offset?: number;
  delay?: number;
  minDuration?: number;
  speed?: number;
  allowScroll?: boolean;
}

export const Route = ({ card, component: Component }: Props) => {
  const [shouldScroll, setShouldScroll] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const dispatch = useDispatch();
  const isLoading = useTypedSelector((state) => state.profile.isLoading);
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false,
  });

  const routeProps = useTypedSelector(
    ({ router }) => router[card].params,
  ) as RouteProps;
  const queryStringRouteMode = useTypedSelector(
    ({ router }) => router.queryStringRoute.mode,
  );
  const queryStringRouteCard = useTypedSelector(
    ({ router }) => router.queryStringRoute.card,
  );

  useEffect(() => {
    const allowScroll =
      routeProps.allowScroll ||
      (card === queryStringRouteCard &&
        queryStringRouteMode === 'READ' &&
        !isLoading);
    if (allowScroll) {
      setShouldScroll(true);
    }
  }, [
    card,
    queryStringRouteCard,
    queryStringRouteMode,
    routeProps.allowScroll,
    isLoading,
  ]);

  useEffect(() => {
    if (shouldScroll) {
      const isCypressTest =
        typeof window !== 'undefined' && typeof window.Cypress !== 'undefined';
      const noMinDuration = typeof routeProps.minDuration === 'undefined';
      const {
        duration = isMobile && noMinDuration ? 1 : undefined,
        offset = 0,
        delay = !isMobile ? 10 : undefined,
        minDuration = !isMobile ? 400 : undefined,
        speed = undefined,
      } = routeProps;
      dispatch(startScroll());
      if (wrapperRef.current !== null) {
        scroll(wrapperRef.current, {
          duration: isCypressTest ? 1 : duration,
          offset,
          delay: isCypressTest ? 1 : delay,
          minDuration: isCypressTest ? 1 : minDuration,
          speed,
        }).then(() => {
          setTimeout(() => {
            dispatch(endScroll());
          }, 100);
        });
        setShouldScroll(false);
      }
    }
  }, [dispatch, isMobile, routeProps, shouldScroll]);

  return (
    <span ref={wrapperRef}>
      <Component routeProps={routeProps} />
    </span>
  );
};
